import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Link,
  CircularProgress,
  AppBar,
  Toolbar,
  useMediaQuery,
  Select,
  MenuItem,
  Menu,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import bg from "../../assets/femme-background.jpg";
import logo from "../../assets/logo-full.png";

// petits drapeaux (PNG discrets)
import frFlag from "../../assets/fr-flag.png";
import ukFlag from "../../assets/uk-flag.png";
import plFlag from "../../assets/pl-flag.png";

const BRAND = "#449F2D";
const BRAND_HOVER = "#388624";

const flagIcons = {
  fr: frFlag,
  en: ukFlag,
  pl: plFlag,
};

export default function Login() {
  const { t, i18n } = useTranslation("pages/authentification");

  const [ident, setIdent] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // état pour le menu de langues mobile
  const [flagAnchor, setFlagAnchor] = useState(null);
  const openFlags = Boolean(flagAnchor);
  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    setFlagAnchor(null);
  };

  // Seuil "AMV" ~800px
  const isNarrow = useMediaQuery("(max-width:800px)");

  const identError = useMemo(() => (ident ? "" : ""), [ident]);
  const canSubmit = ident && pwd && !identError && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setErr("");
    try {
      await new Promise((r) => setTimeout(r, 800));
      window.location.href = "/";
    } catch {
      setErr(t("login.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        bgcolor: isNarrow ? "#fff" : "transparent",
      }}
    >
      {/* Sélecteur de langue — bottom-center en mobile (Button+Menu), top-right en desktop (Select) */}
      {isNarrow ? (
        <Box
          sx={{
            position: "fixed",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1300,
          }}
        >
          <Button
            onClick={(e) => setFlagAnchor(e.currentTarget)}
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderColor: "#e6e6e6",
              color: "#449F2D",
              backgroundColor: "rgba(255,255,255,0.95)",
              borderRadius: "999px",
              px: 1.25,
              py: 0.25,
              minWidth: 82,
              textTransform: "none",
              fontWeight: "bold",
              touchAction: "manipulation",
            }}
          >
            <Box
              component="img"
              src={flagIcons[i18n.language] || frFlag}
              alt="lang"
              sx={{ width: 24, height: 16, objectFit: "cover" }}
            />
            {String(i18n.language || "fr").toUpperCase()}
          </Button>

          <Menu
            anchorEl={flagAnchor}
            open={openFlags}
            onClose={() => setFlagAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            keepMounted
          >
            <MenuItem onClick={() => changeLang("fr")}>
              <img src={frFlag} alt="FR" width="22" height="14" style={{ marginRight: 8 }} />
              Français
            </MenuItem>
            <MenuItem onClick={() => changeLang("en")}>
              <img src={ukFlag} alt="EN" width="22" height="14" style={{ marginRight: 8 }} />
              English
            </MenuItem>
            <MenuItem onClick={() => changeLang("pl")}>
              <img src={plFlag} alt="PL" width="22" height="14" style={{ marginRight: 8 }} />
              Polski
            </MenuItem>
          </Menu>
        </Box>
      ) : (
        <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 1200 }}>
          <Select
            value={i18n.language || "fr"}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            variant="standard"
            disableUnderline
            sx={{
              display: "flex",
              alignItems: "center",
              fontWeight: "bold",
              color: "#449F2D",
              fontSize: "0.95rem",
              minWidth: 82,
              px: 1,
              "& .MuiSelect-icon": { color: "#449F2D", right: 6 },
              backgroundColor: "rgba(255,255,255,0.9)",
              borderRadius: "999px",
              border: "1px solid #e6e6e6",
            }}
            renderValue={(val) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="img" src={flagIcons[val]} alt={val} sx={{ width: 24, height: 16, objectFit: "cover" }} />
                <span style={{ lineHeight: 1 }}>{String(val).toUpperCase()}</span>
              </Box>
            )}
          >
            <MenuItem value="fr">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img src={frFlag} alt="FR" width="22" height="14" /> Français
              </Box>
            </MenuItem>
            <MenuItem value="en">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img src={ukFlag} alt="EN" width="22" height="14" /> English
              </Box>
            </MenuItem>
            <MenuItem value="pl">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img src={plFlag} alt="PL" width="22" height="14" /> Polski
              </Box>
            </MenuItem>
          </Select>
        </Box>
      )}

      {/* AppBar mobile (fond blanc + logo centré) */}
      {isNarrow && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{ backgroundColor: "#fff", color: "#111", borderBottom: "1px solid #eee" }}
        >
          <Toolbar sx={{ minHeight: 64, display: "flex", justifyContent: "center" }}>
            <Link href="/garanties" underline="none" sx={{ display: "inline-block" }}>
              <Box component="img" src={logo} alt="Logo" sx={{ height: 90 }} />
            </Link>
          </Toolbar>
        </AppBar>
      )}

      {/* Background (desktop seulement) */}
      {!isNarrow && (
        <>
          <Box
            aria-hidden
            sx={{
              position: "fixed",
              inset: 0,
              backgroundImage: `url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(1px)",
              transform: "scale(1.02)",
            }}
          />
          <Box aria-hidden sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.18)" }} />
        </>
      )}

      {/* Conteneur : mobile centré, desktop décalé depuis la droite */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: isNarrow ? "center" : "flex-end",
          px: 2,
          pt: isNarrow ? "84px" : 2,
          pr: isNarrow ? 2 : "20vw",
        }}
      >
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: "100%",
            maxWidth: 420,
            mx: { xs: "auto", sm: "auto", md: 0 },
            px: { xs: 3, sm: 5 },
            py: { xs: 3.5, sm: 5 },
            borderRadius: 3,
            backgroundColor: isNarrow ? "#fff" : "rgba(255,255,255,0.96)",
            border: "1px solid #e6e6e6",
            backdropFilter: isNarrow ? "none" : "saturate(120%) blur(6px)",
          }}
        >
          <Stack spacing={2}>
            {/* Logo (desktop) */}
            {!isNarrow && (
              <Box sx={{ display: "grid", placeItems: "center", mt: 0.5 }}>
                <Link href="/garanties" underline="none" sx={{ display: "inline-block" }}>
                  <Box component="img" src={logo} alt="Logo" sx={{ height: { xs: 70, md: 90 } }} />
                </Link>
              </Box>
            )}

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                textAlign: "center",
                color: "#374151",
                fontSize: { xs: 28, sm: 32, md: 34 },
              }}
            >
              {t("login.title")}
            </Typography>

            {err && (
              <Box
                sx={{
                  border: "1px solid #ffd3d3",
                  bgcolor: "#fff5f5",
                  color: "#a30000",
                  borderRadius: 2,
                  p: 1.25,
                  fontSize: 14,
                }}
              >
                {err}
              </Box>
            )}

            <TextField
              placeholder={t("login.clientNumberPlaceholder")}
              value={ident}
              onChange={(e) => setIdent(e.target.value)}
              error={Boolean(identError)}
              helperText={
                identError || (
                  <Link href="/aide/identifiant" underline="hover" sx={{ color: BRAND, fontWeight: 400, fontSize: 12 }}>
                    {t("login.findClientNumber")}
                  </Link>
                )
              }
              FormHelperTextProps={{ sx: { mt: 0.5 } }}
              fullWidth
              size="medium"
              InputProps={{
                sx: {
                  borderRadius: 999,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9ca3af" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                  py: 0.25,
                },
              }}
            />

            <TextField
              placeholder={t("login.passwordPlaceholder")}
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              helperText={
                <Link href="/mot-de-passe-oublie" underline="hover" sx={{ color: BRAND, fontWeight: 400, fontSize: 12 }}>
                  {t("login.forgotPassword")}
                </Link>
              }
              FormHelperTextProps={{ sx: { mt: 0.5 } }}
              fullWidth
              size="medium"
              InputProps={{
                sx: {
                  borderRadius: 999,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9ca3af" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                  py: 0.25,
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={!canSubmit}
              sx={{
                bgcolor: BRAND,
                color: "#fff",
                "&:hover": { bgcolor: BRAND_HOVER, color: "#fff" },
                "&.Mui-disabled": { color: "#fff", opacity: 0.6 },
                py: 1.1,
                fontWeight: 800,
                borderRadius: 999,
                fontSize: 16,
                textTransform: "none",
                alignSelf: "center",
                width: { xs: "68%", sm: "55%" },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : t("login.submit")}
            </Button>

            <Typography variant="body2" sx={{ textAlign: "center", color: "#6b7280" }}>
              {t("login.or")}
            </Typography>

            <Typography variant="body2" sx={{ textAlign: "center", color: "#6b7280", lineHeight: 1.25, mb: -0.5 }}>
              {t("login.firstVisitTitle")}
              <br />
              {t("login.firstVisitSubtitle")}
            </Typography>

            <Button
              href="/register"
              variant="contained"
              disableElevation
              sx={{
                bgcolor: BRAND,
                color: "#fff",
                "&:hover": { bgcolor: BRAND_HOVER, color: "#fff" },
                py: 1.1,
                fontSize: 16,
                fontWeight: 800,
                borderRadius: 999,
                textTransform: "none",
                alignSelf: "center",
                width: { xs: "80%", sm: "72%" },
              }}
            >
              {t("login.createSpace")}
            </Button>

            <Typography variant="body2" textAlign="center" sx={{ mt: 0.5 }}>
              <Link href="/aide/connexion" underline="hover" sx={{ color: BRAND, fontWeight: 600 }}>
                {t("login.helpLogin")}
              </Link>
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}